import { tracker, type Attribution } from "@/lib/tracker";
import type { PageContextLead } from "@/lib/data/page-context";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://internal.electrifiedmarina.com";

export interface FormResponse {
  success: boolean;
  message: string;
  errors?: Record<string, string>;
  customer_id?: number;
}

export type MarketingFormFieldType =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "zip"
  | "select"
  | "radio"
  | "checkbox"
  | "date"
  | "consent";

export interface MarketingFormField {
  id: number;
  label: string;
  field_key: string;
  field_type: MarketingFormFieldType;
  required: boolean;
  options: string[];
  placeholder: string | null;
  help_text: string | null;
  min_length: number | null;
  max_length: number | null;
}

export interface MarketingFormOptIn {
  id: number;
  form_id: number;
  topic_id: number;
  topic_name: string;
  topic_slug: string;
  opt_in_copy: string;
  required: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface PublicMarketingForm {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  include_marketing_use_disclosure?: boolean;
  marketing_use_disclosure?: boolean;
  marketing_use_disclosure_enabled?: boolean;
  marketing_use_disclosure_text?: string | null;
  fields: MarketingFormField[];
  marketing_opt_ins?: MarketingFormOptIn[];
}

export interface MarketingFormSubmissionPayload extends Record<string, unknown> {
  marketing_opt_ins?: Array<number | string>;
}

/** One answer to "How did you hear about us?" (lead attribution spec 4.2). */
export interface HeardAboutUsOption {
  value: string;
  label: string;
}

/** Tracker touches plus the visitor's own answer and the page, sent as the top-level `attribution`. */
type SubmissionAttribution = Attribution & { self_reported_source?: string; page: PageContextLead };

/** What a form adds to its submission's `attribution`. */
export interface SubmissionContext {
  /** The "How did you hear about us?" value, omitted when unanswered. */
  selfReportedSource?: string;
  /** The page's PageContext lead. Forms outside a PageContext default to kind "content" and the path. */
  page?: PageContextLead;
}

/** What actually reaches /api/public/forms/*: the form's fields plus tracking added here. */
type FormSubmissionBody = MarketingFormSubmissionPayload & {
  visitor_id: string;
  attribution?: SubmissionAttribution;
};

function isHeardAboutUsOption(value: unknown): value is HeardAboutUsOption {
  return (
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    "label" in value &&
    typeof value.value === "string" &&
    typeof value.label === "string"
  );
}

/** Options for "How did you hear about us?", or [] if they can't be loaded. */
async function loadHeardAboutUsOptions(): Promise<HeardAboutUsOption[]> {
  try {
    // `next` only applies on the server, which refreshes them like other content.
    const res = await fetch(`${API_BASE}/api/public/lead-attribution/heard-about-us`, { next: { revalidate: 300 } });
    if (!res.ok) return [];
    const body: { data?: unknown } = await res.json();
    return Array.isArray(body.data) ? body.data.filter(isHeardAboutUsOption) : [];
  } catch {
    return [];
  }
}

let heardAboutUsRequest: Promise<HeardAboutUsOption[]> | null = null;

/**
 * Options for "How did you hear about us?", fetched once per page load and
 * shared by every form on it. Resolves to [] on failure so a form drops the
 * question instead of blocking, and the next form to ask tries again.
 * Browser only: on the server the shared request would never refresh.
 */
export function fetchHeardAboutUsOptions(): Promise<HeardAboutUsOption[]> {
  heardAboutUsRequest ??= loadHeardAboutUsOptions().then((options) => {
    if (options.length === 0) heardAboutUsRequest = null;
    return options;
  });
  return heardAboutUsRequest;
}

/** What a server page fetches so a built-in form renders complete in its HTML. */
export interface PreloadedForm {
  form: PublicMarketingForm | null;
  heardAboutUs: HeardAboutUsOption[];
}

/**
 * Fetches a built-in form's definition and its "How did you hear about us?"
 * options on the server. A form that loads in the browser grows from a short
 * placeholder after the page paints, pushing down everything below it.
 */
export async function preloadForm(slug: string): Promise<PreloadedForm> {
  const [form, heardAboutUs] = await Promise.all([fetchPublicForm(slug).catch(() => null), loadHeardAboutUsOptions()]);
  return { form, heardAboutUs };
}

function submissionAttribution({ selfReportedSource, page }: SubmissionContext): SubmissionAttribution | null {
  const touches = tracker.attribution();
  if (!touches) return null;
  const attribution: SubmissionAttribution = {
    ...touches,
    page: page ?? { kind: "content", ref: window.location.pathname },
  };
  if (selfReportedSource) attribution.self_reported_source = selfReportedSource;
  return attribution;
}

export async function fetchPublicForm(slug: string): Promise<PublicMarketingForm | null> {
  // `next` only applies when a server component renders the form, so a page
  // that ships the definition in its HTML refreshes it like other content.
  const res = await fetch(`${API_BASE}/api/public/forms/${encodeURIComponent(slug)}`, { next: { revalidate: 300 } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Unable to load form");
  return res.json();
}

/** Submit a JSON form (inquiry, contact, newsletter, spec-sheet). */
export async function submitForm(
  endpoint: string,
  data: MarketingFormSubmissionPayload,
  context: SubmissionContext = {},
): Promise<FormResponse> {
  const res = await fetch(`${API_BASE}/api/public/forms/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...data,
      visitor_id: tracker.getVisitorId(),
      attribution: submissionAttribution(context) ?? undefined,
    } satisfies FormSubmissionBody),
  });

  const json = await res.json();
  if (!res.ok) {
    return {
      success: false,
      message: json.message || json.error || "Something went wrong. Please try again.",
      errors: json.errors,
    };
  }

  // Link visitor to customer profile
  if (json.customer_id) {
    tracker.identify(json.customer_id);
  }

  return json;
}

/** Submit a multipart form (service, repower — with file uploads) */
export async function submitFormWithFiles(
  endpoint: string,
  data: MarketingFormSubmissionPayload,
  files: File[],
  context: SubmissionContext = {},
): Promise<FormResponse> {
  const formData = new FormData();

  // Include visitor ID for tracking
  formData.append("visitor_id", tracker.getVisitorId());

  // Bracketed keys so PHP parses the same nested `attribution` array a JSON body gives.
  const attribution = submissionAttribution(context);
  if (attribution) {
    for (const touchKey of ["first_touch", "latest_touch"] as const) {
      for (const [key, value] of Object.entries(attribution[touchKey])) {
        if (value) formData.append(`attribution[${touchKey}][${key}]`, value);
      }
    }
    if (attribution.signup_location) {
      formData.append("attribution[signup_location]", attribution.signup_location);
    }
    if (attribution.self_reported_source) {
      formData.append("attribution[self_reported_source]", attribution.self_reported_source);
    }
    for (const [key, value] of Object.entries(attribution.page)) {
      formData.append(`attribution[page][${key}]`, String(value));
    }
  }

  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined && value !== "") {
      if (key === "marketing_opt_ins" && Array.isArray(value)) {
        for (const topic of value) {
          formData.append("marketing_opt_ins[]", String(topic));
        }
      } else {
        formData.append(key, String(value));
      }
    }
  }

  for (const file of files) {
    formData.append("photos[]", file);
  }

  const res = await fetch(`${API_BASE}/api/public/forms/${endpoint}`, {
    method: "POST",
    body: formData,
  });

  const json = await res.json();
  if (!res.ok) {
    return {
      success: false,
      message: json.message || json.error || "Something went wrong. Please try again.",
      errors: json.errors,
    };
  }

  // Link visitor to customer profile
  if (json.customer_id) {
    tracker.identify(json.customer_id);
  }

  return json;
}
